<template>
  <div class="vx-card p-6">
    <base-ag-grid-header
      :rows-selected-count="rowsSelectedCountWithAllSelection"
      :filters-count="filtersAppliedCount"
      :filters-match.sync="filtersMatch"
      @reset-selection="setSelectedRowsNone()"
      @reset-filters="resetFilters()">
      <template v-slot:header-left>
        <items-per-page
          :count="count"
          :currentPage="currentPage"
          :paginationPageSize="paginationPageSize"
          @changed-page-size="paginationSetPageSize">
        </items-per-page>

        <list-action-drop-down
          v-if="isAnyRowsSelected"
          :options="defaultMultipleActionOptions"
          class="ml-3"
          @export="exportData()"
          @delete="confirmDeleteRecords()">
        </list-action-drop-down>
      </template>

      <template v-slot:header-right>
        <page-header-actions
          class="hidden md:flex"
          :actions="headerActions"
          @action="onHeaderAction"
        />
      </template>
    </base-ag-grid-header>

    <selection-alert-info
      v-model="selectionPageAlert"
      class="mb-5"
      :entity="entity"
      :count="count"
      :rows-selected-count="rowsSelectedCount"
      @select-all="setSelectedRowsAll()"></selection-alert-info>

    <export-alert-info
      class="mb-5"
      :entity="$tc(`$Entities.${entity}`, 2)"
      :exporting.sync="exportingList"
      :exported.sync="exportedList"
      :fileUrl="urlFileExported"
      :send-email="exportSendEmail"/>

    <ag-grid-vue
      :key="agGridKey"
      ref="agGridTable"
      :domLayout="domLayout"
      :style="gridStyle"
      :components="components"
      :frameworkComponents="frameworkComponents"
      :gridOptions="gridOptions"
      class="ag-theme-material w-100 ag-grid-table"
      :columnDefs="columnDefs"
      :defaultColDef="defaultColDef"
      :column-types="columnTypes"
      :getRowNodeId="getRowNodeId"
      :autoParamsRefresh="true"
      rowSelection="multiple"
      :animateRows="true"
      :suppressRowClickSelection="true"
      rowModelType="infinite"
      :pagination="hasPagination"
      :paginationPageSize="paginationPageSize"
      :suppressPaginationPanel="suppressPaginationPanel"
      :overlayLoadingTemplate="overlayLoadingTemplate"
      :noRowsOverlayComponent="noRowsOverlayComponent"
      :noRowsOverlayComponentParams="noRowsOverlayComponentParams"
      :maxConcurrentDatasourceRequests="maxConcurrentDatasourceRequests"
      :cacheBlockSize="cacheBlockSize"
      :maxBlocksInCache="maxBlocksInCache"
      @selection-changed="onSelectionChanged"
      @grid-ready="onGridReady"
      @model-updated="onModelUpdate"
      @grid-size-changed="onGridSizeChanged"
      @first-data-rendered="onFirstDataRendered"
      @row-clicked="onRowClicked"
      @row-double-clicked="onRowDoubleClicked"
      @filter-changed="onFilterChanged"
      @pagination-changed="onPaginationChanged">
    </ag-grid-vue>

    <!-- Mobile Sticky Bottom Bar -->
    <div
      class="sticky-bottom-bar-icons md:hidden"
      :style="{ gridTemplateColumns: `repeat(${(isAnyRowsSelected ? 1 : 0) + 2}, 1fr)` }"
    >
      <vs-dropdown
        v-if="isAnyRowsSelected"
        vs-custom-content
        vs-trigger-click
        class="mobile-bar-btn mobile-bar-btn-simple"
      >
        <div class="mobile-bar-btn-content">
          <i class="feather icon-more-horizontal mobile-bar-icon"></i>
          <span class="mobile-btn-label">Actions</span>
        </div>
        <vs-dropdown-menu class="mobile-actions-dropdown gp-sticky-dropdown-menu">
          <ul class="mobile-actions-list">
            <li
              v-for="(opt, idx) in mobileStickyBulkActionOptions"
              :key="`mba-${idx}`"
              class="mobile-actions-item"
              :class="{ disabled: !!opt.disabled }"
              @click="opt.disabled ? null : onMobileBulkAction(opt.event)"
            >
              <span class="flex items-center">
                <feather-icon :icon="opt.icon" svgClasses="h-4 w-4" class="mr-2" />
                <span>{{ opt.label }}</span>
              </span>
            </li>
          </ul>
        </vs-dropdown-menu>
      </vs-dropdown>

      <button
        class="mobile-bar-btn mobile-bar-btn-simple"
        type="button"
        @click="newItem"
      >
        <i class="feather icon-plus mobile-bar-icon"></i>
        <span class="mobile-btn-label">Add</span>
      </button>

      <button
        class="mobile-bar-btn mobile-bar-btn-simple mobile-bar-btn-refresh"
        type="button"
        @click="resetDataSourceAndSelection()"
      >
        <i class="feather icon-refresh-cw mobile-bar-icon"></i>
        <span class="mobile-btn-label">Refresh</span>
      </button>
    </div>

    <vs-popup
      :title="titleModal"
      :active.sync="activeModalCreateOrEdit">
      <transition name="zoom-fade">
        <attribute-list-create-or-edit
          v-if="showCreateOrEditComponent"
          :entity="entity"
          :operation="operation"
          :model-payload="recordSelected"
          :actions="actionsOnCreateOrEdit"
          @action="onActionFromCreateOrEdit"
          @model-was-changed="(val) => this.createOrEditModelWasChanged=val"
          @saved="savedItemOnAgGridTable"
          @close="activeModalCreateOrEdit=false">
        </attribute-list-create-or-edit>
      </transition>
    </vs-popup>

    <vs-popup
      :title="exportModalTitle"
      :active.sync="activeModalToExport">
      <transition name="zoom-fade">
        <export-json-to-excel
          v-if="showExportComponent"
          :columns="getColumnsToExport"
          :data="rowsSelected"
          @close="activeModalToExport=false"
          @export="onExport">
        </export-json-to-excel>
      </transition>
    </vs-popup>
  </div>
</template>

<script>
import '@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss';
import { mapActions } from 'vuex';

// Mixins
import commonListCreateOrEditWithAgGrid from '@/views/modules/_mixins/commonListCreateOrEditWithAgGrid';

// Cell Renderer
import CellRendererActions from '@/views/modules/_components/cell-renderer/SingleCellRendererActions.vue';

// Custom Components
import BaseAgGridHeader from '@/views/modules/_components/BaseAgGridHeader.vue';
import ItemsPerPage from '@/views/modules/_components/ItemsPerPage.vue';
import ExportJsonToExcel from '@/views/modules/_components/ExportJsonToExcel.vue';
import ListActionDropDown from '@/views/modules/_components/ListActionDropDown.vue';
import AttributeListCreateOrEdit from '@/views/modules/attribute/AttributeListCreateOrEdit.vue';
import AgGridSelectionHeader from '@/views/modules/_components/AgGridSelectionHeader';
import ExportAlertInfo from '@/views/modules/_components/ExportAlertInfo.vue';
import SelectionAlertInfo from '@/views/modules/SelectionAlertInfo.vue';
import CustomNoRowsOverlay from '@/views/modules/_components/customAgGridNoRowsOverlay';
import PageHeaderActions from '@/views/modules/_components/PageHeaderActions.vue';
/**
 * Component to list and manage contact attributes
 *
 * @module views/modules/audience/attribute/AttributeList
 * @author Dilan Useche <dilan8810@gmail.com>
 */
export default {
  name: 'AttributeList',
  i18n: {
    messages: {
      en: {
        ThereAreNoAttributes: 'No custom attributes to display',
        $NoRowsOverlay: {
          Title: 'No custom attributes to display',
          Text: 'There is nothing here to view right now. Create a new custom attribute to get started.',
          Button: 'Create new custom attribute',
        },
      },
    },
  },
  components: {
    BaseAgGridHeader,
    AttributeListCreateOrEdit,
    ItemsPerPage,
    ListActionDropDown,
    ExportJsonToExcel,
    ExportAlertInfo,
    SelectionAlertInfo,
    PageHeaderActions,
    // eslint-disable-next-line vue/no-unused-components
    CellRendererActions,
  },
  mixins: [commonListCreateOrEditWithAgGrid],
  data() {
    return {
      entity: this.$enums.Entity.CONTACT_ATTRIBUTE,
      cloneDisabled: true,
      exportDisabled: true,
      exportCollectionFunction: this.exportAttributes,
      deleteRecordFunction: this.removeAttribute,
      deleteRecordsFunction: this.removeAttributes,
      preferFitColumns: true,
      noRowsMessage: this.$t('ThereAreNoAttributes'),
      columnDefs: [
        {
          colId: 'name',
          headerName: this.$t('$General.Name'),
          field: 'name',
          filter: true,
          minWidth: 250,
          maxWidth: 1906,
          checkboxSelection: true,
          headerComponentParams: { checkboxSelection: true },
        },
        {
          colId: 'type',
          headerName: this.$tc('$General.Type'),
          valueGetter: (params) => (params.data
            ? this.$t(`$Enums.$Attributes.$Type.${params.data.type}`)
            : ''),
          field: 'type',
          filter: true,
          minWidth: 250,
          maxWidth: 650,
        },
      ],
      components: {
        CellRendererActions,
      },
      frameworkComponents: {
        agColumnHeader: AgGridSelectionHeader,
        customNoRowsOverlay: CustomNoRowsOverlay,
      },
    };
  },
  computed: {
    mobileStickyBulkActionOptions() {
      const options = [...this.defaultMultipleActionOptions];
      const canEdit = !this.editDisabled;

      if (canEdit) {
        options.unshift({
          label: this.$t('$General.Edit'),
          event: this.$enums.Operation.EDIT,
          icon: 'Edit2Icon',
          disabled: this.selectedAllRows || this.rowsSelectedCount !== 1,
        });
      }

      return options;
    },
    headerActions() {
      return [
        {
          id: 'add',
          tooltip: this.$t('$General.AddNew'),
          kind: 'icon',
          icon: 'icon-plus',
        },
        {
          id: 'refresh',
          tooltip: 'Refresh',
          kind: 'icon',
          icon: 'icon-refresh-cw',
        },
      ];
    },
  },
  beforeMount() {
    const $this = this;
    this.noRowsOverlayComponent = 'customNoRowsOverlay';
    this.noRowsOverlayComponentParams = {
      title: this.$t('$NoRowsOverlay.Title'),
      text: this.$t('$NoRowsOverlay.Text'),
      buttonText: this.$t('$NoRowsOverlay.Button'),
      async actionButtonClicked() {
        await $this.newItem();
      },
    };
  },
  methods: {
    onMobileBulkAction(event) {
      switch (event) {
        case this.$enums.Operation.EDIT:
          if (this.rowsSelectedCount === 1 && this.rowsSelected && this.rowsSelected[0]) {
            this.editRecord(this.rowsSelected[0]);
          }
          break;
        case 'export':
          this.exportData();
          break;
        case 'delete':
          this.confirmDeleteRecords();
          break;
        default:
          break;
      }
    },
    onHeaderAction({ id }) {
      switch (id) {
        case 'add':
          this.newItem();
          break;
        case 'refresh':
          this.resetDataSourceAndSelection();
          break;
        default:
          break;
      }
    },
    ...mapActions({
      fetchAllAttributes: 'attribute/fetchAllAttributes',
      exportAttributes: 'attribute/exportFile',
      removeAttribute: 'attribute/removeAttribute',
      removeAttributes: 'attribute/removeAttributes',
    }),
    async fetchAgGridData(params) {
      return this.fetchAllAttributes(params);
    },
  },
};

</script>

<style scoped lang="scss">
// Mobile Sticky Bottom Bar (icon buttons)
.sticky-bottom-bar-icons {
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 2px solid #E5E7EB;
  padding: 8px 12px;
  display: grid;
  gap: 8px;
  box-shadow: 0 -4px 6px -1px rgba(0, 0, 0, 0.1);
  z-index: 100;
  margin: 0 -1.5rem -1.5rem; // match vx-card p-6

  @media (max-width: 767px) {
    position: -webkit-sticky;
    position: sticky;
  }
}

.mobile-bar-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.15s ease;
  color: #6B7280;
  font-family: inherit;
  outline: none;

  &:active {
    transform: scale(0.95);
    background: rgba(139, 92, 246, 0.1);
  }
}

.mobile-bar-btn-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.mobile-bar-icon {
  font-size: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 6px auto;
  width: 22px;
  height: 22px;
}

.mobile-btn-label {
  font-size: 11px;
  font-weight: 500;
  text-align: center;
  line-height: 1;
}

.mobile-bar-btn-simple {
  @media (hover: hover) {
    &:hover {
      background: rgba(139, 92, 246, 0.05);
    }
  }

  &:active {
    background: rgba(139, 92, 246, 0.1) !important;
  }
}

.mobile-bar-btn-refresh {
  @media (hover: hover) {
    &:hover {
      background: rgba(139, 92, 246, 0.05);
    }
  }

  &:active {
    background: rgba(139, 92, 246, 0.1) !important;
  }

  &:not(:active):not(:hover) {
    background: transparent !important;
  }
}

.mobile-actions-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.mobile-actions-item {
  padding: 10px 12px;
  cursor: pointer;

  &:hover {
    background: rgba(139, 92, 246, 0.06);
  }
}
</style>
